import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { 
  Upload, 
  Camera, 
  X, 
  Image as ImageIcon,
  AlertCircle,
  Check
} from 'lucide-react';
import { uploadImageMenuPhoto, addImageMenuItem } from '@/lib/firebase/imageMenus';
import type { ImageMenuItem } from '@/types';

interface ImageMenuUploadProps {
  restaurantId: string;
  currentImageCount: number;
  maxImages: number;
  onImageUploaded: (image: ImageMenuItem) => void;
  onUploadComplete: () => void;
  onUploadingChange: (uploading: boolean) => void;
}

interface FileWithPreview {
  file: File;
  preview: string;
  id: string;
  uploading: boolean;
  progress: number;
  error?: string;
  uploaded?: boolean;
}

/**
 * ImageMenuUpload - Handle image uploads for image menu
 * Features:
 * - Drag and drop interface
 * - Multiple file selection
 * - Camera capture on mobile
 * - Progress tracking
 * - File validation
 * - Preview with removal option
 */
const ImageMenuUpload: React.FC<ImageMenuUploadProps> = ({
  restaurantId,
  currentImageCount,
  maxImages,
  onImageUploaded,
  onUploadComplete,
  onUploadingChange
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const lastClickTime = useRef<number>(0);

  const generateFileId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
  const remainingSlots = maxImages - currentImageCount;

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return 'Only JPEG, PNG, and WebP images are allowed';
    }
    if (file.size > maxFileSize) {
      return 'File size must be less than 10MB';
    }
    return null;
  };

  const openFileDialog = () => {
    const now = Date.now();
    
    // Prevent rapid successive clicks (debounce with 500ms)
    if (isFileDialogOpen || (now - lastClickTime.current < 500)) {
      console.log('File dialog already open or too soon, ignoring click');
      return;
    }
    
    lastClickTime.current = now;
    setIsFileDialogOpen(true);
    
    console.log('Opening file dialog');
    fileInputRef.current?.click();
    
    // Reset the flag after dialog closes (longer timeout for macOS)
    setTimeout(() => {
      setIsFileDialogOpen(false);
      console.log('File dialog flag reset');
    }, 1000);
  };

  const handleFileSelection = (files: FileList | null) => {
    if (!files) return;

    setError(null);
    const fileArray = Array.from(files);
    
    // Check if adding these files would exceed the limit
    const totalFiles = selectedFiles.length + fileArray.length;
    if (totalFiles > remainingSlots) {
      setError(`Can only add ${remainingSlots} more images. You selected ${fileArray.length} files.`);
      return;
    }

    const newFiles: FileWithPreview[] = [];
    
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        setError(error);
        continue;
      }

      const fileWithPreview: FileWithPreview = {
        file,
        preview: URL.createObjectURL(file),
        id: generateFileId(),
        uploading: false,
        progress: 0
      };
      
      newFiles.push(fileWithPreview);
    }

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };  const removeFile = (fileId: string) => {
    setSelectedFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      // Clean up preview URL
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return updated;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    handleFileSelection(files);
  };

  // Touch event handlers for mobile drag and drop
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    // For touch devices, we'll trigger file input instead of actual file drop
    // since DataTransfer is not available in touch events
    // But add the same protection as click
    if (!isFileDialogOpen) {
      openFileDialog();
    }
  };

  const uploadFile = async (fileWithPreview: FileWithPreview): Promise<ImageMenuItem | null> => {
    try {
      // Update file state to uploading
      setSelectedFiles(prev => 
        prev.map(f => 
          f.id === fileWithPreview.id 
            ? { ...f, uploading: true, progress: 0, error: undefined }
            : f
        )
      );

      // Upload to Firebase Storage
      const { url, thumbnailUrl, metadata } = await uploadImageMenuPhoto(
        restaurantId,
        fileWithPreview.file,
        (progress) => {
          setSelectedFiles(prev => 
            prev.map(f => 
              f.id === fileWithPreview.id 
                ? { ...f, progress }
                : f
            )
          );
        }
      );

      // Get current index for this file upload
      const currentIndex = selectedFiles.findIndex(f => f.id === fileWithPreview.id);
      const orderIndex = currentImageCount + currentIndex;

      // Add to Firestore with the calculated order
      const imageMenuItem = await addImageMenuItem(restaurantId, {
        imageUrl: url,
        thumbnailUrl,
        fileName: metadata.fileName,
        fileSize: metadata.fileSize,
        dimensions: metadata.dimensions
      }, orderIndex);

      // Update file state to uploaded
      setSelectedFiles(prev => 
        prev.map(f => 
          f.id === fileWithPreview.id 
            ? { ...f, uploading: false, uploaded: true, progress: 100 }
            : f
        )
      );

      return imageMenuItem;
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      // Update file state with error
      setSelectedFiles(prev => 
        prev.map(f => 
          f.id === fileWithPreview.id 
            ? { ...f, uploading: false, error: errorMessage }
            : f
        )
      );
      
      return null;
    }
  };  const uploadAllFiles = async () => {
    if (selectedFiles.length === 0) return;

    onUploadingChange(true);
    setError(null);

    const filesToUpload = selectedFiles.filter(f => !f.uploaded && !f.error);
    const uploadPromises = filesToUpload.map(file => uploadFile(file));

    try {
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(result => result !== null) as ImageMenuItem[];
      
      // Notify parent of successful uploads
      successfulUploads.forEach(image => onImageUploaded(image));
      
      // Clean up preview URLs and reset state
      selectedFiles.forEach(file => URL.revokeObjectURL(file.preview));
      
      setTimeout(() => {
        setSelectedFiles([]);
        onUploadComplete();
      }, 1000);
      
    } catch (error) {
      setError('Some uploads failed. Please try again.');
    } finally {
      onUploadingChange(false);
    }
  };

  const hasValidFiles = selectedFiles.length > 0;
  const hasUploadedFiles = selectedFiles.some(f => f.uploaded);
  const hasErrors = selectedFiles.some(f => f.error);
  const isUploading = selectedFiles.some(f => f.uploading);

  return (
    <div className="relative w-full max-w-2xl max-h-[90vh] transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all flex flex-col">
      {/* Header - Fixed */}
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Upload Images</h3>
          <p className="text-sm text-gray-600 mt-1">
            Add up to {remainingSlots} more images ({currentImageCount}/{maxImages} used)
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onUploadComplete}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2"
          >
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors mb-6 cursor-pointer ${
            dragActive
              ? 'border-primary bg-primary/10'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={openFileDialog}
        >
          <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Upload className="w-8 h-8 text-gray-400" />
          </div>
          
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            {window.innerWidth <= 768 ? 'Tap to select images' : 'Drag & drop images here'}
          </h4>
          <p className="text-gray-600 mb-6">
            Or click to browse files (JPEG, PNG, WebP up to 10MB each)
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <Button
              variant="primary"
              onClick={openFileDialog}
              leftIcon={<ImageIcon className="w-4 h-4" />}
            >
              Choose Files
            </Button>
            
            <Button
              variant="secondary"
              onClick={() => cameraInputRef.current?.click()}
              leftIcon={<Camera className="w-4 h-4" />}
              className="sm:hidden"
            >
              Take Photo
            </Button>
          </div>
        </div>        {/* File Previews */}
        {hasValidFiles && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-4"
          >
            <h4 className="font-medium text-gray-900">
              Selected Files ({selectedFiles.length})
            </h4>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {selectedFiles.map((file) => (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative bg-gray-50 rounded-lg overflow-hidden"
                >
                  <div className="aspect-square">
                    <img
                      src={file.preview}
                      alt={file.file.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Overlay for upload state */}
                  {file.uploading && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-white text-xs">{file.progress}%</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Success state */}
                  {file.uploaded && (
                    <div className="absolute inset-0 bg-green-500 bg-opacity-75 flex items-center justify-center">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                  )}
                  
                  {/* Error state */}
                  {file.error && (
                    <div className="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-white" />
                    </div>
                  )}
                  
                  {/* Remove button */}
                  {!file.uploading && !file.uploaded && (
                    <button
                      onClick={() => removeFile(file.id)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  
                  {/* File info */}
                  <div className="p-2">
                    <p className="text-xs text-gray-600 truncate">{file.file.name}</p>
                    {file.error && (
                      <p className="text-xs text-red-600 mt-1">{file.error}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Footer - Fixed */}
      {hasValidFiles && (
        <div className="border-t border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-600">
              {hasErrors ? (
                <span className="text-red-600">Some files have errors</span>
              ) : hasUploadedFiles ? (
                <span className="text-green-600">Upload complete!</span>
              ) : (
                <span>Ready to upload {selectedFiles.filter(f => !f.error).length} files</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-end space-x-3">
            <Button
              variant="ghost"
              onClick={() => {
                selectedFiles.forEach(file => URL.revokeObjectURL(file.preview));
                setSelectedFiles([]);
              }}
              disabled={isUploading}
            >
              Clear All
            </Button>
            
            <Button
              variant="primary"
              onClick={uploadAllFiles}
              disabled={isUploading || selectedFiles.every(f => f.uploaded || f.error)}
              loading={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload Images'}
            </Button>
          </div>
        </div>
      )}
      
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => {
          console.log('File input onChange triggered');
          handleFileSelection(e.target.files);
          
          // Reset file input and dialog state
          if (e.target) {
            e.target.value = '';
          }
          setIsFileDialogOpen(false);
        }}
        onClick={(e) => {
          // Prevent any additional clicks when dialog is already open
          if (isFileDialogOpen) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          console.log('File input focused');
        }}
        onBlur={() => {
          console.log('File input blurred');
          // Reset dialog state when input loses focus
          setTimeout(() => setIsFileDialogOpen(false), 100);
        }}
        className="hidden"
      />
      
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileSelection(e.target.files)}
        className="hidden"
      />
    </div>
  );
};

export default ImageMenuUpload;